import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import SignatureCanvas from 'react-signature-canvas';
import { inputStyle, labelStyle, btnOutline, btnPrimary } from '../ui';

const MAX_SIRKA_PODPISU = 900;   // uložený podpis se zmenší (menší PNG, podpis se vejde na PDF)
const OKRAJ_PODPISU = 12;
const JE_IPHONE = typeof navigator !== 'undefined' && /iPhone|iPod/.test(navigator.userAgent);

// Ořízne nakreslený podpis na okolí tahů a zmenší ho; vrací PNG data URL (null = nic nenakresleno).
function ulozPodpis(canvas) {
  const { width: w, height: h } = canvas;
  if (!w || !h) return null;
  const data = canvas.getContext('2d').getImageData(0, 0, w, h).data;
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3] > 0) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  }
  if (x1 < 0) return null;
  x0 = Math.max(0, x0 - OKRAJ_PODPISU); y0 = Math.max(0, y0 - OKRAJ_PODPISU);
  x1 = Math.min(w - 1, x1 + OKRAJ_PODPISU); y1 = Math.min(h - 1, y1 + OKRAJ_PODPISU);
  const sw = x1 - x0 + 1, sh = y1 - y0 + 1;
  const meritko = Math.min(1, MAX_SIRKA_PODPISU / sw);
  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(sw * meritko));
  out.height = Math.max(1, Math.round(sh * meritko));
  out.getContext('2d').drawImage(canvas, x0, y0, sw, sh, 0, 0, out.width, out.height);
  return out.toDataURL('image/png');
}

function naSirku() {
  return typeof window === 'undefined' || !window.matchMedia || window.matchMedia('(orientation: landscape)').matches;
}

function useNaSirku() {
  const [sirka, setSirka] = useState(naSirku());
  useEffect(() => {
    const mq = window.matchMedia && window.matchMedia('(orientation: landscape)');
    if (!mq) return undefined;
    const zmena = () => setSirka(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', zmena) : mq.addListener(zmena);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', zmena) : mq.removeListener(zmena); };
  }, []);
  return sirka;
}

// Podpis přes celou obrazovku (tlačítka Hotovo / Smazat). Tahy se drží jako vektory, takže po otočení
// zařízení (změně velikosti plátna) se podpis jen přepočítá a nic se neztratí.
// Android: zkusí se celá obrazovka + orientace na šířku (screen.orientation.lock; jinde se tiše ignoruje).
// iPhone: orientaci nejde zamknout, při otočení na výšku se zobrazí výzva k otočení.
function PodpisCelaObrazovka({ pocatecniTahy, onHotovo, onZavrit }) {
  const platno = useRef(null);
  const oblast = useRef(null);
  const tahy = useRef(pocatecniTahy || null);   // { data, w, h }
  const [rozmer, setRozmer] = useState({ w: 0, h: 0 });
  const [neprazdne, setNeprazdne] = useState(!!pocatecniTahy);
  const [vyzvaZavrena, setVyzvaZavrena] = useState(false);
  const naSirku = useNaSirku();

  useEffect(() => {
    let zamceno = false;
    const el = document.documentElement;
    (async () => {
      try {
        if (!JE_IPHONE && el.requestFullscreen && !document.fullscreenElement) await el.requestFullscreen();
        if (!JE_IPHONE && window.screen?.orientation?.lock) { await window.screen.orientation.lock('landscape'); zamceno = true; }
      } catch (e) { /* nepodporováno nebo zamítnuto – podpis funguje v aktuální orientaci */ }
    })();
    return () => {
      try { if (zamceno) window.screen.orientation.unlock(); } catch (e) { /* nic */ }
      try { if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(() => {}); } catch (e) { /* nic */ }
    };
  }, []);

  useEffect(() => {
    const zmer = () => {
      const w = Math.floor(oblast.current.clientWidth);
      const h = Math.floor(oblast.current.clientHeight);
      if (w && h) setRozmer(r => (r.w === w && r.h === h ? r : { w, h }));
    };
    zmer();
    const ro = new ResizeObserver(zmer);
    ro.observe(oblast.current);
    return () => ro.disconnect();
  }, []);

  // po změně rozměru plátno zůstane prázdné: tahy se zvětší/zmenší (poměr stran se zachová) a vykreslí znovu
  useEffect(() => {
    if (!rozmer.w || !platno.current) return;
    const t = tahy.current;
    if (!t || !t.data.length) return;
    if (t.w === rozmer.w && t.h === rozmer.h) { platno.current.fromData(t.data); return; }
    const s = Math.min(rozmer.w / t.w, rozmer.h / t.h);
    const ox = (rozmer.w - t.w * s) / 2, oy = (rozmer.h - t.h * s) / 2;
    const data = t.data.map(skupina => skupina.map(p => ({ ...p, x: p.x * s + ox, y: p.y * s + oy })));
    tahy.current = { data, w: rozmer.w, h: rozmer.h };
    platno.current.fromData(data);
  }, [rozmer]);

  const poTahu = () => {
    const c = platno.current;
    tahy.current = { data: c.toData().map(skupina => skupina.map(p => ({ ...p }))), w: rozmer.w, h: rozmer.h };   // kopie (pad drží živé pole)
    setNeprazdne(!c.isEmpty());
  };
  const smazat = () => { platno.current.clear(); tahy.current = null; setNeprazdne(false); };
  const hotovo = () => {
    const png = ulozPodpis(platno.current.getCanvas());
    if (!png) return;
    onHotovo(png, tahy.current);
  };

  const vyzva = JE_IPHONE && !naSirku && !vyzvaZavrena;
  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: '#fff', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '8px 12px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
        <strong style={{ fontSize: '16px' }}>Podpis</strong>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button type="button" style={{ ...btnOutline, minHeight: '44px' }} onClick={smazat} disabled={!neprazdne}>Smazat</button>
          <button type="button" style={{ ...btnPrimary, minHeight: '44px', opacity: neprazdne ? 1 : 0.5 }} onClick={hotovo} disabled={!neprazdne}>Hotovo</button>
        </div>
      </div>
      {vyzva && (
        <div style={{ background: '#fef3c7', color: '#92400e', padding: '8px 12px', fontSize: '14px', display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center' }}>
          <span>📱 Pro pohodlnější podpis otočte telefon na šířku.</span>
          <button type="button" style={{ background: 'none', border: 'none', color: '#92400e', fontSize: '18px', cursor: 'pointer' }} aria-label="Zavřít výzvu" onClick={() => setVyzvaZavrena(true)}>✕</button>
        </div>
      )}
      <div ref={oblast} style={{ flex: 1, minHeight: 0, position: 'relative', margin: '10px', border: '2px dashed #9ca3af', borderRadius: '8px', overflow: 'hidden', touchAction: 'none', lineHeight: 0 }}>
        {!neprazdne && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '18px', pointerEvents: 'none', lineHeight: 1.3, textAlign: 'center', padding: '12px' }}>Podepište se prstem nebo perem</div>}
        {rozmer.w > 0 && (
          <SignatureCanvas ref={platno} penColor="#111" minWidth={1} maxWidth={3.5} onEnd={poTahu}
            canvasProps={{ width: rozmer.w, height: rozmer.h, style: { display: 'block', touchAction: 'none' } }} />
        )}
      </div>
      <div style={{ padding: '0 12px calc(8px + env(safe-area-inset-bottom))', display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" style={{ ...btnOutline, minHeight: '40px' }} onClick={onZavrit}>Zrušit</button>
      </div>
    </div>,
    document.body,
  );
}

// Jméno osoby na prodejně + podpis. Klepnutí na podpisové pole otevře podpis přes celou obrazovku;
// po „Hotovo“ se podpis vrátí sem jako náhled a zůstane zachovaný. Rodič čte hodnoty přes ref
// (vrací { jmeno, podpis } | { chyba }). onZmena({ jmeno, podpis }) hlásí, co je vyplněno.
const PodpisBlok = React.forwardRef(function PodpisBlok({ onZmena }, ref) {
  const [jmeno, setJmeno] = useState('');
  const [podpis, setPodpis] = useState(null);     // PNG data URL (oříznutý) k odeslání
  const [tahy, setTahy] = useState(null);         // vektorové tahy k úpravě podpisu
  const [otevreno, setOtevreno] = useState(false);

  const hlas = (j, jePodpis) => onZmena && onZmena({ jmeno: j.trim().length > 0, podpis: jePodpis });

  React.useImperativeHandle(ref, () => ({
    hodnoty: () => {
      if (!jmeno.trim()) return { chyba: 'Napište jméno osoby, která se podepisuje.' };
      if (!podpis) return { chyba: 'Podpis je prázdný.' };
      return { jmeno: jmeno.trim(), podpis };
    },
  }));

  const hotovo = (png, t) => { setPodpis(png); setTahy(t); setOtevreno(false); hlas(jmeno, true); };
  const smazat = () => { setPodpis(null); setTahy(null); hlas(jmeno, false); };

  return (
    <div>
      <label style={labelStyle}>Jméno osoby na prodejně</label>
      <input style={inputStyle} value={jmeno} onChange={e => { setJmeno(e.target.value); hlas(e.target.value, !!podpis); }}
        placeholder="Jméno a příjmení" autoComplete="off" />
      <label style={labelStyle}>Podpis (klepnutím otevřete podpis přes celou obrazovku)</label>
      <div role="button" tabIndex={0} onClick={() => setOtevreno(true)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setOtevreno(true); }}
        style={{ border: '2px dashed #9ca3af', borderRadius: '8px', background: '#fff', width: '100%', boxSizing: 'border-box', height: '160px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', overflow: 'hidden' }}>
        {podpis
          ? <img src={podpis} alt="Podpis" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          : <span style={{ color: '#6b7280', fontSize: '16px', padding: '8px', textAlign: 'center' }}>✍️ Klepněte sem a podepište se</span>}
      </div>
      <button type="button" style={{ ...btnOutline, marginTop: '8px' }} onClick={smazat} disabled={!podpis}>Smazat podpis</button>
      {otevreno && <PodpisCelaObrazovka pocatecniTahy={tahy} onHotovo={hotovo} onZavrit={() => setOtevreno(false)} />}
    </div>
  );
});

export default PodpisBlok;
